import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useCancelJob, useCreateJob, useJob, useJobChunkStream } from "@/lib/api/hooks";
import { StatusBadge } from "@/components/status-badge";
import { formatDuration, formatRelative, formatTokens } from "@/lib/format";
import { ArrowLeft, RotateCw, Ban } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/jobs/$id")({
  component: JobDetail,
  head: ({ params }) => ({ meta: [{ title: `Job ${params.id.slice(0, 8)} — CSaaS` }] }),
});

function JobDetail() {
  const { id } = Route.useParams();
  const { data: job, isLoading, error } = useJob(id);
  const cancelMutation = useCancelJob();
  const createMutation = useCreateJob();
  const navigate = useNavigate();

  useJobChunkStream(id);

  if (isLoading && !job) {
    return (
      <div className="max-w-2xl">
        <Button variant="ghost" size="sm" asChild className="mb-4">
          <Link to="/jobs">
            <ArrowLeft className="h-4 w-4" /> Jobs
          </Link>
        </Button>
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="max-w-2xl">
        <Button variant="ghost" size="sm" asChild className="mb-4">
          <Link to="/jobs">
            <ArrowLeft className="h-4 w-4" /> Jobs
          </Link>
        </Button>
        <p className="text-muted-foreground">{error?.message ?? "Job not found."}</p>
      </div>
    );
  }

  const cancel = () => {
    cancelMutation.mutate(job.id, {
      onSuccess: () => toast.message("Job cancelled"),
      onError: (err) => toast.error(err.message),
    });
  };

  const rerun = () => {
    createMutation.mutate(
      {
        skillName: job.skillName,
        prompt: job.prompt,
        priority: job.priority,
        timeoutSeconds: job.timeoutSeconds,
        context: job.context ?? null,
      },
      {
        onSuccess: (next) => {
          toast.success("Re-queued");
          navigate({ to: "/jobs/$id", params: { id: next.id } });
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
            <Link to="/jobs">
              <ArrowLeft className="h-4 w-4" /> All jobs
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-semibold tracking-tight">
              <Link
                to="/skills/$name"
                params={{ name: job.skillName }}
                className="hover:text-primary transition-colors"
              >
                {job.skillName}
              </Link>
            </h1>
            <StatusBadge status={job.status} />
          </div>
          <code className="text-[11px] text-muted-foreground font-mono">{job.id}</code>
        </div>
        <div className="flex gap-2">
          {job.status === "queued" && (
            <Button
              variant="outline"
              size="sm"
              onClick={cancel}
              disabled={cancelMutation.isPending}
            >
              <Ban className="h-4 w-4" /> Cancel
            </Button>
          )}
          {(job.status === "complete" || job.status === "failed" || job.status === "cancelled") && (
            <Button size="sm" onClick={rerun} disabled={createMutation.isPending}>
              <RotateCw className="h-4 w-4" /> Re-run
            </Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">Prompt</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="whitespace-pre-wrap text-sm font-sans">
              {job.prompt || (
                <span className="text-muted-foreground">(not stored client-side)</span>
              )}
            </pre>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">Run details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5 text-sm">
            <Row label="Priority" value={job.priority} />
            <Row label="Timeout" value={`${job.timeoutSeconds}s`} />
            <Row label="Created" value={formatRelative(job.createdAt)} />
            {job.startedAt && <Row label="Started" value={formatRelative(job.startedAt)} />}
            {job.completedAt && <Row label="Completed" value={formatRelative(job.completedAt)} />}
            <Row label="Duration" value={formatDuration(job.durationMs)} />
            <Row label="Tokens" value={formatTokens(job.tokensUsed)} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            Result stream
            {job.status === "running" && (
              <span className="h-1.5 w-1.5 rounded-full bg-info animate-pulse" />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {job.error ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive font-mono">
              {job.error}
            </div>
          ) : job.result ? (
            <pre className="rounded-md bg-muted p-4 text-xs font-mono leading-relaxed overflow-x-auto whitespace-pre-wrap">
              {job.result}
            </pre>
          ) : job.chunks.length === 0 && job.status === "queued" ? (
            <div className="text-sm text-muted-foreground">
              Waiting for a worker to pick up this job…
            </div>
          ) : job.chunks.length === 0 ? (
            <div className="text-sm text-muted-foreground">No output yet.</div>
          ) : (
            <pre className="rounded-md bg-muted p-4 text-xs font-mono leading-relaxed overflow-x-auto whitespace-pre-wrap">
              {job.chunks.join("")}
            </pre>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground text-xs capitalize">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
